import React, { useCallback, useEffect, useState } from "react";
import { Play, Trash2 } from "lucide-react";

import { AdminScreen, Field, Section, UploadButton } from "@/admin/kit";
import { api } from "@/lib/api";
import { Button, Spinner } from "@/components/kit";
import { TutorialVideo } from "@/components/widgets";
import { useApp } from "@/store";

const sm = "!min-h-[44px]";

function Row({ v, onChanged }) {
  const { toast } = useApp();
  const [name, setName] = useState(v.name);
  const [confirm, setConfirm] = useState(false);
  const [play, setPlay] = useState(false);
  const id = `admin-video-${v.id}`;
  const act = async (fn, msg) => {
    try {
      await fn();
      toast(msg, "success");
      onChanged();
    } catch (e) {
      toast(e.message, "error");
    }
  };
  return (
    <Section testId={id} title={v.name} note={`${(v.size / 1024 / 1024).toFixed(1)}MB · used by: ${v.used_by.map((s) => s.name).join(", ") || "no styles"}`}>
      {play ? <TutorialVideo url={v.url} /> : <Button testId={`${id}-play`} title="Preview" icon={Play} variant="ghost" onClick={() => setPlay(true)} className={`${sm} self-start`} />}
      <div className="flex items-end gap-2">
        <div className="flex-1"><Field testId={`${id}-name`} label="Name" value={name} onChange={setName} /></div>
        <Button testId={`${id}-rename`} title="Rename" variant="outline" onClick={() => act(() => api(`/admin/videos/${v.id}`, { method: "PUT", body: { name } }), "Renamed.")} className={sm} />
      </div>
      <div className="flex flex-wrap gap-2">
        <UploadButton kind="video" testId={`${id}-replace`} title="Replace file" path={`/admin/videos/${v.id}/replace`} onUploaded={() => { toast("Replaced — styles updated.", "success"); onChanged(); }} onError={(m) => toast(m, "error")} />
        {confirm ? (
          <>
            <Button testId={`${id}-delete-confirm`} title="Confirm delete" icon={Trash2} onClick={() => act(() => api(`/admin/videos/${v.id}`, { method: "DELETE" }), "Deleted.")} className={`${sm} !bg-[#EF4444]`} />
            <Button testId={`${id}-delete-cancel`} title="Cancel" variant="ghost" onClick={() => setConfirm(false)} className={sm} />
          </>
        ) : <Button testId={`${id}-delete`} title="Delete" icon={Trash2} variant="ghost" onClick={() => setConfirm(true)} className={sm} />}
      </div>
    </Section>
  );
}

export default function AdminVideos() {
  const { toast } = useApp();
  const [items, setItems] = useState(null);
  const load = useCallback(() => {
    api("/admin/videos").then(setItems).catch((e) => toast(e.message, "error"));
  }, [toast]);
  useEffect(load, [load]);
  return (
    <AdminScreen title="Video library" testId="admin-videos">
      <p className="t-caption">Attach videos to a style from its hairstyle editor. Replacing a file updates every style using it.</p>
      <UploadButton kind="video" testId="admin-videos-upload" title="Upload new video" onUploaded={() => { toast("Uploaded.", "success"); load(); }} onError={(m) => toast(m, "error")} />
      {!items ? <Spinner /> : items.length ? (
        <div className="grid items-start gap-5 md:grid-cols-2">{items.map((v) => <Row key={v.id} v={v} onChanged={load} />)}</div>
      ) : <p data-testid="admin-videos-empty" className="t-body">No videos yet.</p>}
    </AdminScreen>
  );
}
